import { Criterion, CriterionListItem, ListValueType, Range } from '@app/api';
import { useCallback } from 'react';
import {
  isValidRange,
  getRangeMax,
  divideQuotas,
} from '/lib/criterionQuotaUtils';
import { setCriterionValues } from 'lib/criteria';

export const useAddQuotaButtonHandler = ({
  showQuotas,
  criterion,
  onChange,
  numberOfParticipantRequired = 0,
  options = [],
}: {
  showQuotas: boolean;
  criterion: Criterion;
  onChange: (c: Criterion) => void;
  numberOfParticipantRequired: number;
  options?: any[];
}) => {
  return useCallback(() => {
    if (showQuotas) {
      onChange({ ...criterion, quotas: null });
    } else {
      let updated: Criterion | null = null;
      if (isEmptyAllowList(criterion.allowList)) {
        updated = { ...criterion };
        updated = setCriterionValues(
          criterion,
          options.map((o) => o.value),
        );
      }

      const newCriterion = updated ? updated : criterion;
      onChange({ ...newCriterion, quotas: [] });
      if (isAgeRangeItem(newCriterion)) {
        const range = newCriterion.allowList![0] as Range;
        const min = range.min ?? 0;
        const max = getRangeMax(range.max);
        const mid = Math.floor((min + (max === Infinity ? 100 : max)) / 2);
        const divided = divideQuotas(numberOfParticipantRequired, 2);
        onChange({
          ...newCriterion,
          quotas: [
            { item: { min: min, max: mid }, value: divided.shift() ?? 0 },
            {
              item: {
                min: mid + 1,
                max: max === Infinity ? null : max || null,
              },
              value: divided.shift() ?? 0,
            },
          ],
        });
      } else {
        const divided = divideQuotas(
          numberOfParticipantRequired ?? 0,
          newCriterion.allowList?.length ?? 0,
        );
        onChange({
          ...newCriterion,
          quotas: newCriterion.allowList?.map((field: CriterionListItem) => {
            return { item: field, value: divided.shift() ?? 0 };
          }),
        });
      }
    }
  }, [showQuotas, onChange, criterion, numberOfParticipantRequired, options]);
};

export const useOnCriterionChange = ({
  showQuotas,
  criterion,
  onChange,
  numberOfParticipantRequired = 0,
}: {
  showQuotas: boolean;
  criterion: Criterion;
  onChange: (c: Criterion) => void;
  numberOfParticipantRequired: number;
}) => {
  return useCallback(
    (newCriterion: Criterion) => {
      if (!showQuotas) {
        onChange(newCriterion);
        return;
      }
      if (
        newCriterion.allowList === null ||
        newCriterion.allowList?.length === 0
      ) {
        onChange({
          ...newCriterion,
          quotas: null,
        });
        return;
      }
      const allowList = newCriterion.allowList || [];
      const oldQuotas = Array.isArray(criterion.quotas) ? criterion.quotas : [];
      const quotas = oldQuotas.filter((q) => allowList.includes(q.item));

      const existingQuotaSum = quotas.reduce((sum, q) => sum + q.value, 0);
      const remainingTotal =
        (numberOfParticipantRequired ?? 0) - existingQuotaSum;
      const newValues = allowList.filter(
        (field) => !quotas.some((q) => q.item === field),
      );

      if (newValues.length > 0) {
        const divided = divideQuotas(remainingTotal, newValues.length);
        newValues.forEach((field, idx) => {
          quotas.push({ item: field, value: divided[idx] });
        });
      }

      onChange({
        ...newCriterion,
        quotas,
      });
    },
    [showQuotas, onChange, criterion, numberOfParticipantRequired],
  );
};

function isEmptyAllowList(
  allowList: CriterionListItem[] | null | undefined,
): boolean {
  return !allowList || allowList.length === 0;
}

function isAgeRangeItem(criterion: Criterion): boolean {
  return (
    criterion.listValueType === ListValueType.AGE_RANGE &&
    criterion.allowList?.length === 1 &&
    isValidRange(criterion.allowList[0])
  );
}
